import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiBell, FiCheckCircle, FiCircle, FiTrash2 } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../Api/api';
import { getAuthToken } from '../Api/authToken';
import './NotificationBell.css';

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

const NotificationBell = () => {
  const resolveImageUrl = (image) => {
    if (!image) return '';
    if (image.startsWith('http://') || image.startsWith('https://')) return image;
    if (image.startsWith('/uploads/')) return `${API_URL.replace('/api', '')}${image}`;
    if (image.startsWith('uploads/')) return `${API_URL.replace('/api', '')}/${image}`;
    return `${API_URL.replace('/api', '')}/uploads/${image}`;
  };

 const resolveLink = (n) => {
    const raw = n?.link || '';
    if (!raw) return '';
    
    // If it's an external URL, return as is
    if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;

    // FIX: Change /properties/ to /property/
    let processedLink = raw;
    if (raw.startsWith('/properties/')) {
      processedLink = raw.replace('/properties/', '/property/');
    }

    const isAdmin = window.location.pathname.startsWith('/admin');
    const isPartner = window.location.pathname.startsWith('/partner');

    // Handle Admin/Partner specific redirects if necessary
    if (isAdmin && processedLink.startsWith('/property/')) {
      const id = processedLink.split('/').pop();
      return `/admin/properties?search=${id}`;
    }
    if (isPartner && processedLink.startsWith('/property/')) {
      const id = processedLink.split('/').pop();
      return `/partner/properties?search=${id}`;
    }

    return processedLink;
  };

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const panelRef = useRef(null);

  const unreadCount = useMemo(() => items.filter((n) => !n.is_read).length, [items]);
  const sortByLatest = (list) => [...list].sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
  const formatDateTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    const onClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;

    axios.get(`${API_URL}/notifications`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setItems(sortByLatest(Array.isArray(res.data) ? res.data : [])))
      .catch(() => {});

    let es = null;
    let reconnectTimer = null;
    const connect = () => {
      es = new EventSource(`${API_URL}/notifications/stream?token=${encodeURIComponent(token)}`);
      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data || '{}');
          if (data?.id) {
            setItems((prev) => {
              const merged = [data, ...prev.filter((x) => x.id !== data.id)].slice(0, 100);
              return sortByLatest(merged);
            });
          }
        } catch (_) {}
      };
      es.onerror = () => {
        es?.close();
        reconnectTimer = window.setTimeout(connect, 3000);
      };
    };
    connect();
    return () => {
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      es?.close();
    };
  }, []);

  useEffect(() => {
    const registerPush = async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      const token = getAuthToken();
      if (!token) return;
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;
        const sw = await navigator.serviceWorker.register('/service-worker.js');
        await navigator.serviceWorker.ready;
        const keyRes = await axios.get(`${API_URL}/notifications/vapid-public-key`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const publicKey = keyRes.data?.publicKey;
        if (!publicKey) return;
        const existing = await sw.pushManager.getSubscription();
        const subscription = existing || await sw.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey)
        });
        await axios.post(`${API_URL}/notifications/subscribe`, { subscription }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.log('Web push setup failed:', err?.message || err);
      }
    };
    registerPush();
  }, []);

  const markReadToggle = async (notification) => {
    const token = getAuthToken();
    if (!token) return;
    const nextRead = !notification.is_read;
    setItems((prev) => prev.map((n) => (n.id === notification.id ? { ...n, is_read: nextRead } : n)));
    try {
      await axios.patch(`${API_URL}/notifications/${notification.id}/${nextRead ? 'read' : 'unread'}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (_) {}
  };

  const clearAll = async () => {
    const token = getAuthToken();
    if (!token) return;
    setItems([]);
    try {
      await axios.delete(`${API_URL}/notifications/clear`, { headers: { Authorization: `Bearer ${token}` } });
    } catch (_) {}
  };

  const markAllRead = async () => {
    const token = getAuthToken();
    if (!token) return;
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    try {
      await axios.patch(`${API_URL}/notifications/mark-all-read`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch (_) {}
  };

  const openLink = (n) => {
    const next = resolveLink(n);
    if (!next) return;
    window.location.href = next;
  };

  return (
    <div className="notif-wrap" ref={panelRef}>
      <button className="notif-trigger-btn" type="button" onClick={() => setOpen((v) => !v)}>
        <FiBell />
        {unreadCount > 0 && <span className="notif-count">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>
      {open && (
        <div className="notif-panel">
          <div className="notif-head">
            <strong>Notifications</strong>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="notif-mini-btn" type="button" onClick={markAllRead}>Read all</button>
              <button className="notif-mini-btn" type="button" onClick={clearAll}><FiTrash2 /></button>
            </div>
          </div>
          <div className="notif-list">
            {items.length === 0 && <p className="notif-empty">No notifications</p>}
            {items.map((n) => (
              <div key={n.id} className={`notif-item ${n.is_read ? 'read' : 'unread'}`}>
                {n.image ? <img src={resolveImageUrl(n.image)} alt="" className="notif-thumb" onError={(e) => { e.currentTarget.src = '/logo.jpg'; }} /> : <div className="notif-thumb empty" />}
                <div className="notif-body" onClick={() => openLink(n)}>
                  <p className="notif-title">{n.title}</p>
                  <p className="notif-msg">{n.message}</p>
                  {n.event_type === 'new_arrival' ? (
                    <>
                      <p className="notif-time-label">Date &amp; Time</p>
                      <p className="notif-time">{formatDateTime(n.createdAt)}</p>
                    </>
                  ) : (
                    <p className="notif-time">{formatDateTime(n.createdAt)}</p>
                  )}
                </div>
                <button className="notif-mark-btn" type="button" onClick={() => markReadToggle(n)} title={n.is_read ? 'Mark unread' : 'Mark read'}>
                  {n.is_read ? <FiCircle /> : <FiCheckCircle />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
